import { stringify } from "querystring"
import { useMemo } from "react"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { SMOOTH_LINE_API_URL } from "src/lib/constants"
import useSWR from "swr"

/**
 * 店舗一覧系のエンドポイントが共通で返す項目。
 * 2分割の項目を返さない `two_payment_pdfs/stores` のレスポンス型はこちらを使う。
 */
export type StoreInListBase = {
  id: number
  name: string
  nameKatakana?: string | null
  companyName: string
  customerAcquisitionSupportEnabled: boolean
  inStoreUsageEnabled: boolean
  storeAuth?: {
    effective_now: boolean
  }
}

/** `/base_api/smoother/stores` のレスポンス。2分割の2項目はこのエンドポイントだけが返す */
export type StoreInList = StoreInListBase & {
  /** 支払回数の選択肢の拡張（2分割）の対象事業者に属する店舗かどうか（適用開始前も含む） */
  numberOfPaymentsExtensionTarget: boolean
  /** 2分割が適用される日時（ISO形式。対象外の場合は null） */
  numberOfPaymentsExtensionEffectiveAt: string | null
}

type Pagination = {
  current_page: number
  limit_value: number
  total_count: number
  total_pages: number
}

export type UseStoresSWROptions = {
  /** 店舗名・店舗名カタカナの部分一致（API の q） */
  q?: string
  /** 2分割の対象事業者に属する店舗のみに絞り込む（適用開始前のレコードも対象に含む） */
  numberOfPaymentsExtensionTarget?: boolean
}

export const useStoresSWR = (options?: UseStoresSWROptions) => {
  const q = options?.q?.trim() || undefined
  const numberOfPaymentsExtensionTarget = options?.numberOfPaymentsExtensionTarget ?? false

  const query = useMemo(
    () => ({
      page: 1,
      per: 100000, // 現状はページネーションをしていないし、ならとりあえずここまではいかないであろうという値を設定しておきます
      ...(q ? { q } : {}),
      ...(numberOfPaymentsExtensionTarget ? { number_of_payments_extension_target: true } : {}),
    }),
    [q, numberOfPaymentsExtensionTarget]
  )

  const url = useMemo(
    () => `${SMOOTH_LINE_API_URL}/base_api/smoother/stores?${stringify(query)}`,
    [query]
  )
  const { getWithToken } = useSmoothApi()

  const fetcher = async () => {
    const res = await getWithToken(url)
    const data = res.data as {
      stores: StoreInList[]
      pagination: Pagination
    }
    return data
  }

  const storesSwr = useSWR(url, fetcher, {
    revalidateIfStale: true,
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
  })

  return {
    storesSwr,
  }
}
